import { ChevronDownIcon, ChevronRightIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import { useMemo } from 'react';
import { T } from '../../../../../../Translations';
import { ExperienceResource, isSystemManaged, ProductCollectionResource } from '../Types';
import DraggableProgram from './DraggableProgram';

// GripVertical icon (matches lucide-react design)
const GripVertical = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="9" cy="12" r="1" />
    <circle cx="9" cy="5" r="1" />
    <circle cx="9" cy="19" r="1" />
    <circle cx="15" cy="12" r="1" />
    <circle cx="15" cy="5" r="1" />
    <circle cx="15" cy="19" r="1" />
  </svg>
);

interface DraggableSectionProps {
  section: ProductCollectionResource;
  index: number;
  draggableId: string;
  programCount: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  experienceMap: Record<number, ExperienceResource>;
}

function DraggableSection({
  section,
  index,
  draggableId,
  programCount,
  isExpanded,
  onToggleExpand,
  experienceMap,
}: DraggableSectionProps) {
  const sectionName = section.payload.name || `Section ${index + 1}`;
  const systemManaged = isSystemManaged(section.payload.kind);

  const sectionExperiences = useMemo(() => {
    return section.payload.experiences
      .map((expId) => experienceMap[expId])
      .filter((exp): exp is NonNullable<typeof exp> => exp !== undefined);
  }, [section.payload.experiences, experienceMap]);

  return (
    <Draggable draggableId={draggableId} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`bg-card border border-border rounded-md mb-3 transition-opacity ${
            snapshot.isDragging ? 'opacity-50' : 'opacity-100'
          }`}
        >
          {/* Section Header */}
          <div className="flex items-center gap-3 p-4">
            {/* Drag Handle */}
            <div
              {...provided.dragHandleProps}
              className="cursor-move"
              data-intercom-target="section-reorder-handle"
            >
              <GripVertical className="w-5 h-5 text-muted-foreground" />
            </div>

            <button
              type="button"
              onClick={onToggleExpand}
              className="flex-1 flex items-center justify-between text-left hover:opacity-80 transition-opacity"
            >
              <div>
                <h4 style={{ fontWeight: 'var(--font-weight-medium)' }}>{sectionName}</h4>
                {systemManaged ? (
                  <div
                    className="flex items-center gap-1 text-muted-foreground"
                    style={{ fontSize: 'var(--text-sm)' }}
                  >
                    <LockClosedIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    <span>
                      <T kind="System Managed" />
                    </span>
                  </div>
                ) : (
                  <p className="text-muted-foreground" style={{ fontSize: 'var(--text-sm)' }}>
                    {programCount} {programCount === 1 ? <T kind="Course" /> : <T kind="Courses" />}
                  </p>
                )}
              </div>
              {/* Expand/Collapse Toggle */}
              {!systemManaged && (
                <>
                  {isExpanded ? (
                    <ChevronDownIcon className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
                  ) : (
                    <ChevronRightIcon
                      className="w-5 h-5 text-muted-foreground"
                      aria-hidden="true"
                    />
                  )}
                </>
              )}
            </button>
          </div>

          {/* Expanded Programs List - only for non-system-managed sections */}
          {!systemManaged && isExpanded && (
            <Droppable droppableId={`programs-${index}`} type="PROGRAM">
              {(droppableProvided, droppableSnapshot) => (
                <div
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                  className={`border-t px-3 py-2 transition-colors ${
                    droppableSnapshot.isDraggingOver
                      ? 'bg-primary/10 border-primary/30'
                      : 'bg-muted/50 border-border'
                  }`}
                >
                  {sectionExperiences.length === 0 ? (
                    <div
                      className={`text-sm italic py-2 rounded border-2 border-dashed text-center ${
                        droppableSnapshot.isDraggingOver
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-transparent text-muted-foreground'
                      }`}
                    >
                      {droppableSnapshot.isDraggingOver ? (
                        <T kind="Drop here to add" />
                      ) : (
                        <T kind="No courses in this section" />
                      )}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {sectionExperiences.map((experience, expIndex) => (
                        <DraggableProgram
                          key={experience.payload.id}
                          experience={experience}
                          index={expIndex}
                          draggableId={`program-${index}-${experience.payload.id}`}
                        />
                      ))}
                    </div>
                  )}
                  {droppableProvided.placeholder}
                </div>
              )}
            </Droppable>
          )}
        </div>
      )}
    </Draggable>
  );
}

export default DraggableSection;
